import React, { useEffect, useState } from "react";
import { serverErrorText } from "../lib/serverErrorText";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { returnState } from "../lib/returnState";
import { DownloadSimple } from "@phosphor-icons/react";

import { useWorkflow } from "../workflow/WorkflowContext";
import { exportAllowed, exportTooltip } from "../workflow/workflowState";
import { reviewUnresolvedCount } from "../lib/filingReadiness";
import { downloadCalcXlsx, downloadIossXml, downloadNonUnionOssXml, downloadOssXml, lintOssXml, getCountryReturnSummary, getCalcCoverage, getFxAmendments } from "../lib/api";
import { authClient } from "../lib/authApi";
import { OssLintReviewModal } from "./OssLintReviewModal";
import { PrimaryCta } from "./PrimaryCta";
import { ManualRateCorrectionsChip } from "./ManualRateCorrectionsChip";
import { ReconciliationPanel } from "./ReconciliationPanel";
import { CountryReturnsChecklistRow } from "./CountryReturnsChecklistRow";
import { ChecklistRow } from "./FilingChecklistRow";
import { FilingChecklist } from "./FilingChecklist";
import { FilingPackagePanel } from "./FilingPackagePanel";
import { FreezeConfirmDialog } from "./FreezeConfirmDialog";
import { SupersedeConfirmDialog } from "./SupersedeConfirmDialog";
import { NoActiveCalcNotice } from "./NoActiveCalcNotice";
import { ExportSafetyNotice, ExportErrorNotice } from "./ExportSafetyNotice";
import { errorMessage } from "../lib/errorMessage";
import { guardSupersede } from "../workflow/useGuardedSupersede";
import { isSignedOff } from "../workflow/calcStatus";   // A2-581 lead 35

export const Step6FreezeExport = ({ active }) => {
  const wf = useWorkflow();
  const { calc, setCalc, state, setActiveStep, settings, review, clients } = wf;
  const { plan: sup, supersede } = guardSupersede(calc?.id, wf); // A1-93: carry the form's rates
  const { t } = useTranslation();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [exportBreaches, setExportBreaches] = useState(null);
  const [exportSafety, setExportSafety] = useState(null);
  const nav = useNavigate();
  const location = useLocation();
  const [iossExporting, setIossExporting] = useState(false);
  // v0.6.8 fix — IOSS/OSS/Non-Union-OSS export failures used to share
  // the XLSX export's error box, which renders below ReconciliationPanel
  // + FilingPackagePanel — off-screen from the button that failed.
  // Clicking "Download IOSS XML" with no MSI configured genuinely
  // returned a clear error ("IOSS Member State of Identification not
  // configured...") but it rendered somewhere the operator never
  // scrolled to, reading as the button silently doing nothing
  // (operator-reported 2026-09-04). Give each export its own error so
  // it can render right at its own row.
  const [iossExportError, setIossExportError] = useState(null);
  const [ossExporting, setOssExporting] = useState(false);
  const [ossExportError, setOssExportError] = useState(null);
  const [nonUnionOssExportError, setNonUnionOssExportError] = useState(null);
  // v0.5.0i+21 — Non-Union OSS export flag; placed with the other
  // export state hooks so React's hook-order rule stays intact.
  const [nonUnionOssExporting, setNonUnionOssExporting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [lint, setLint] = useState({ ok: true, offender_count: 0, offenders: [] });
  const [lintModalOpen, setLintModalOpen] = useState(false);
  const [crSummary, setCrSummary] = useState(null);
  // v0.5.0h — freeze action wired directly into Step 6 row 2.
  // The button used to navigate back to Step 5; per the UX
  // simplification pass it now calls the freeze endpoint here so
  // the label matches the outcome.
  const [freezing, setFreezing] = useState(false);
  const [freezeError, setFreezeError] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [fxAmend, setFxAmend] = useState(null);
  const [freezeConfirmOpen, setFreezeConfirmOpen] = useState(false);
  // v0.5.9q — net-negative (repayment) period: credit notes exceeded
  // sales, so the return's output VAT is negative. Legitimate under §17
  // UStG, but the operator must consciously confirm a repayment return.
  const [repaymentConfirmed, setRepaymentConfirmed] = useState(false);
  // v0.5.9 Q3 — refund channel-validation mismatch: a refund inherited its
  // paired original's channel, but the §2 table would route the refund's own
  // attributes elsewhere. Inheritance is authoritative, BUT a mismatch means
  // the original may carry a pre-Q2 mis-routing — the operator must
  // consciously acknowledge before the return files (same pattern as the
  // net-negative advisory).
  const [mismatchAcknowledged, setMismatchAcknowledged] = useState(false);
  // v0.6.5 — general-purpose "Supersede this filing" action. The
  // backend endpoint (POST /runs/calc/{id}/supersede) always existed
  // and already powers the Inspect-drawer's narrow "drop one bad
  // transaction" path, but no button on this screen ever exposed the
  // plain, general form of it (reruns the same period cleanly, no
  // exclusions) — an operator who wanted to redo an already-frozen
  // period had no obvious, tracked way to do it, only an informal
  // re-upload with no reason/audit link back to the frozen calc.
  const [supersedeOpen, setSupersedeOpen] = useState(false);
  const [supersedeReason, setSupersedeReason] = useState("");
  const [supersedeValid, setSupersedeValid] = useState(false);
  const [superseding, setSuperseding] = useState(false);
  const [supersedeError, setSupersedeError] = useState(null);

  // Fetch the OSS lint whenever the active calc changes. Cheap
  // backend call; runs once per calc id mount.
  useEffect(() => {
    if (!active || !calc?.id) return;
    let cancelled = false;
    (async () => {
      const r = await lintOssXml(calc.id);
      if (!cancelled) setLint(r || { ok: true, offender_count: 0, offenders: [] });
    })();
    return () => { cancelled = true; };
  }, [active, calc?.id]);

  // v0.5.0d+4 — Country-return summary fetch (parallel to OSS lint).
  useEffect(() => {
    if (!active || !calc?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const s = await getCountryReturnSummary(calc.id);
        if (!cancelled) setCrSummary(s);
      } catch {
        // Backend may legitimately return nothing if the calc has
        // no country activity (e.g. fully OSS). Leave crSummary
        // null in that case — the export gate then falls back to
        // the old `frozen && filingRef` rule.
      }
    })();
    return () => { cancelled = true; };
  }, [active, calc?.id]);

  // v0.5.2 — Country-return summary fetch (parallel to OSS lint).
  useEffect(() => {
    if (!active || !calc?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const c = await getCalcCoverage(calc.id);
        if (!cancelled) setCoverage(c);
      } catch { /* no coverage info — leave null */ }
      try {
        const a = await getFxAmendments(calc.id);
        if (!cancelled) setFxAmend(a);
      } catch { /* no amendments — leave null */ }
    })();
    return () => { cancelled = true; };
  }, [active, calc?.id]);

  if (!active) return null;
  if (!calc) return <NoActiveCalcNotice onGoToUpload={() => setActiveStep(1)} />;

  const baseAllowed = exportAllowed(state);
  // v0.5.0d+4 — Country-return breach gate. The backend export
  // endpoint already 409s on any breach; the frontend gate must
  // honestly reflect that BEFORE the click so the button doesn't
  // look enabled when the export is guaranteed to fail. Three
  // pieces of UI on this same screen (Step 4 banner, Reconciliation
  // panel, this button) now agree.
  const crBlocked = (crSummary?.countries || []).filter(
    (c) => !c.ready_to_file,
  );
  const crBlockedCount = crBlocked.length;
  const crBlockedCodes = crBlocked.map((c) => c.country).join(", ");
  const negativeCountries = (crSummary?.countries || []).filter(
    (c) => c.net_negative_output,
  );
  const hasNetNegative = negativeCountries.length > 0;
  // v0.5.9 Q3 — refund channel-validation mismatch count (calc-level).
  const refundChannelMismatchCount = crSummary?.refund_channel_mismatch || 0;
  const hasRefundChannelMismatch = refundChannelMismatchCount > 0;
  const allowed = baseAllowed && crBlockedCount === 0;
  const exportBlockTip = crBlockedCount > 0
    ? `Download blocked — ${crBlockedCount} country return(s) failed filing checks (${crBlockedCodes}). Open each one and either assign the flagged rows to that country's manual-other-rates box (DE, FR), or replace the calculation with one that drops bad rows (ES, UK, IT).`
    : null;
  const signedOff = isSignedOff(calc);
  // A1-93 — inputs changed since this calc ran: it must not be locked.
  const staleBlock = state?.derived?.calcStale ? state.steps[6].blockers[0] : null;
  const frozen = !!calc?.frozen;
  // A2-581 lead 25: signed, then the review re-opened (a new question, an
  // answer withdrawn): the lock stayed enabled and the server refused it
  // ("flagged row(s) still need a ..."). It now waits, saying why.
  // Review of 60f2634c: only what the lock route refuses (open per-row
  // decisions, routes_freeze open_family_b_summary), and the real way out -
  // a signed calc takes no more answers, so a re-run, not "return to Step 4".
  const openRows = state?.counters?.familyBOpen || 0;
  const reviewBlock = signedOff && !frozen && openRows > 0
    ? t("ui.freeze.review_reopened", { count: openRows,
        defaultValue: `${openRows} row(s) still need a decision, and a signed-off calculation's decisions are fixed. Run the calculation again (Step 3) and sign off the new run.` })
    : null;
  const replacedBlock = !frozen && calc?.superseded_by_calc_id
    ? t("ui.freeze.replaced_title", { defaultValue: "A newer calculation has replaced this one; it cannot be locked." }) : null;
  const lockBlock = replacedBlock || staleBlock || reviewBlock;
  const filingRef = calc?.filing_reference || null;
  const hasIossRows = (calc?.bucket_counts?.IOSS_SCHEME || 0) > 0;
  // v0.6.11 — was gated on OSS_DISTANCE_SALE, which no classifier
  // writes, so this button never appeared. Art 369g(1) covers both.
  const hasOssRows = ((calc?.bucket_counts?.DISTANCE_SALE || 0)
    + (calc?.bucket_counts?.OSS_SERVICE || 0)) > 0;
  // v0.5.0i+21 — Non-Union OSS row count for gating the XML button.
  // Backend classifier writes bucket=EU_NON_UNION_OSS for UK / non-EU
  // sellers of Art-58 digital services routed to the Non-Union OSS
  // regime. Empty → no filing needed for this period.
  const hasNonUnionOssRows = (calc?.bucket_counts?.EU_NON_UNION_OSS || 0) > 0;
  // v0.4.37 L3 — FC Transfer obligations summary from /api/runs/calc.
  // Backend returns null when calc has no FC_TRANSFER rows (L3E=a).
  const fcSummary = calc?.fc_transfer_summary || null;

  // v0.4.25 D1 — Filing readiness state, sourced live from Settings
  // + workflow context + calc.
  //
  // v0.6.8 fix — msi/imNumber/ossRegs used to read from the global
  // `settings` object (/api/settings), a single-tenant-era source the
  // v0.4.34+ migration moved onto each client's own doc (same stale-read
  // bug WorkflowContext.jsx's `registrations` effect already fixed for
  // company_vat_numbers) — this gate could never be satisfied no matter
  // what an operator entered on their client's IOSS section (reported
  // live 2026-09-04). Look up by the CALC's client_id, not the live
  // selector — the operator may have switched clients since it ran.
  const activeClient = (clients || []).find((c) => c.id === calc?.client_id);
  const msi = (activeClient?.ioss_msi_country || "").trim().toUpperCase();
  const imNumber = (activeClient?.ioss_im_number || "").trim().toUpperCase();
  const ossRegs = (activeClient?.company_vat_numbers || []).filter((e) => {
    const rt = (e.registration_type || "").toUpperCase();
    return rt === "OSS_UNION" || rt === "OSS_NON_UNION" || rt === "OSS";
  });
  const readiness = {
    signedOff, frozen,
    msiConfigured: msi.length === 2,
    imNumberConfigured: /^IM\d{10}$/.test(imNumber),
    hasOssRegistration: ossRegs.length > 0,
    reconciliationBalanced: (calc?.reconciliation?.deltas || []).length === 0,
    hasUnresolvedFlags: reviewUnresolvedCount(review) > 0,
    hasMissingFx: !!calc?.has_missing_fx,
    hasIossRows, hasOssRows,
  };

  const handleExport = async () => {
    if (!calc?.id || exporting || !allowed) return;
    setExportError(null); setExportBreaches(null); setExportSafety(null); setExporting(true);
    const r = await downloadCalcXlsx(calc.id);
    setExporting(false);
    if (!r.ok) {
      setExportError(r.error || "Export failed");
      setExportBreaches(r.breaches || null);
    } else {
      setExportSafety(r.safetyWarnings || null);
    }
  };
  // v0.4.36 Item-1 — Copy filing reference + audit URL.
  // Operator picks 1B=c (markdown link) + 1C=a (inline 2s confirm).
  const handleCopyReference = async () => {
    if (!filingRef || !calc?.id) return;
    const url = `${window.location.origin}/?calc_id=${calc.id}`;
    const md = `[${filingRef}](${url})`;
    try {
      await navigator.clipboard.writeText(md);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) { /* clipboard refused — leave silent */ }
  };
  const handleIossExport = async () => {
    if (!calc?.id || iossExporting || !frozen || !hasIossRows) return;
    setIossExportError(null); setIossExporting(true);
    const r = await downloadIossXml(calc.id);
    setIossExporting(false);
    if (!r.ok) setIossExportError(r.error || "IOSS XML export failed");
  };
  const handleOssExport = async () => {
    if (!calc?.id || ossExporting || !frozen || !hasOssRows) return;
    setOssExportError(null); setOssExporting(true);
    const r = await downloadOssXml(calc.id);
    setOssExporting(false);
    if (!r.ok) setOssExportError(r.error || "OSS XML export failed");
  };
  // v0.5.0i+21 Item 3 — Non-Union OSS export handler.
  const handleNonUnionOssExport = async () => {
    if (!calc?.id || nonUnionOssExporting || !frozen || !hasNonUnionOssRows) return;
    setNonUnionOssExportError(null); setNonUnionOssExporting(true);
    const r = await downloadNonUnionOssXml(calc.id);
    setNonUnionOssExporting(false);
    if (!r.ok) setNonUnionOssExportError(r.error || "Non-Union OSS XML export failed");
  };
  // v0.5.0h — Step 6 wires the freeze action directly. Previously
  // this row's button navigated back to Step 5 even though it
  // claimed "Freeze now →" — exactly the dishonest-label pattern
  // the UX simplification pass is removing.
  const handleFreeze = () => {
    if (!calc?.id || freezing || !signedOff || frozen || lockBlock) return;
    // Radix onOpenChange does not fire for a programmatic (controlled) open,
    // so reset the advisory acknowledgements here to guarantee a re-opened
    // dialog always starts unchecked (freeze re-gated).
    setRepaymentConfirmed(false);
    setMismatchAcknowledged(false);
    setFreezeError(null);
    setFreezeConfirmOpen(true);
  };
  const doFreeze = async () => {
    if (!calc?.id || freezing || !signedOff || frozen || lockBlock) return;
    setFreezeError(null);
    setFreezing(true);
    try {
      const { data } = await authClient.post(`/runs/calc/${calc.id}/freeze`);
      // v0.6.8 fix — the freeze endpoint returns a small partial record
      // ({calc_id, frozen, frozen_at, filing_reference, operator_name,
      // signoff_id} — no `id`, no `run_ids`, no `signed_off_at`), not
      // the full calc. `setCalc(data)` used to REPLACE the whole calc
      // with that partial object, so `calc.run_ids` went undefined —
      // mismatching `selectedRunIds` and tripping the auto-invalidate
      // effect in WorkflowContext, which wiped calc back to null right
      // after a successful freeze. The page then re-derived from a null
      // calc and reset to its unsigned default state (operator-reported
      // 2026-09-04: "when I freeze it throws me back to sign off ...
      // blank!!!"), even though the freeze had already succeeded on the
      // backend. Merge onto the existing calc instead of replacing it.
      setCalc({ ...calc, ...data, id: calc.id });
      setFreezeConfirmOpen(false);
    } catch (e) {
      // v0.6.12 — was `detail.error` first, so the dialog printed the
      // machine code ("REQUIRED_INPUT_NOT_ENTERED") instead of the
      // server's own sentence. See lib/serverErrorText.js.
      setFreezeError(serverErrorText(e?.response?.data?.detail, e.message));
    } finally {
      setFreezing(false);
    }
  };

  const handleSupersedeClick = () => {
    if (!calc?.id || !frozen || calc?.superseded_by_calc_id || sup.blocked) return;
    setSupersedeReason("");
    setSupersedeValid(false);
    setSupersedeError(null);
    setSupersedeOpen(true);
  };
  const doSupersede = async () => {
    if (!calc?.id || superseding || !supersedeValid || sup.blocked) return;
    setSupersedeError(null);
    setSuperseding(true);
    try {
      const data = await supersede({
        reason: supersedeReason.trim(),
        exclude_txn_ids: [],
      });
      setSupersedeOpen(false);
      if (data?.new_calc_id) {
        nav(`/?calc_id=${data.new_calc_id}`);
      }
    } catch (e) {
      setSupersedeError(errorMessage(e));
    } finally {
      setSuperseding(false);
    }
  };

  // v0.4.92 (3b) — Prerequisites that previously lived inside the
  // separate FilingReadinessPanel now hang off the workflow row
  // that gates them. Settings-bound prereqs use a hyperlink fix;
  // workflow-bound prereqs (`goto`) call the stepper. Each prereq
  // carries the same data-testid the readiness panel used, so the
  // testing agent's per-prereq assertions keep working.
  const reviewFlagsPrereq = {
    id: "review-flags",
    label: t("ui.readiness.flags"),
    done: !readiness.hasUnresolvedFlags,
    warning: true,
    sublabel: readiness.hasUnresolvedFlags
      ? t("ui.readiness.flags_todo")
      : t("ui.readiness.flags_done"),
    goto: 4,
  };
  const fxPrereq = {
    id: "fx-rates",
    label: t("ui.readiness.fx"),
    done: !readiness.hasMissingFx,
    warning: true,
    sublabel: readiness.hasMissingFx
      ? t("ui.readiness.fx_todo")
      : t("ui.readiness.fx_done"),
    goto: 2,
  };
  const reconPrereq = {
    id: "reconciliation",
    label: t("ui.readiness.reconciliation"),
    done: readiness.reconciliationBalanced,
    sublabel: readiness.reconciliationBalanced
      ? t("ui.readiness.reconciliation_done")
      : t("ui.readiness.reconciliation_todo"),
    goto: 4,
  };
  // v0.6.8 fix — these pointed to "/settings" (credentials, rate
  // verification), not where IOSS fields live (/admin/clients) —
  // operator-reported 2026-09-04. Deep-link to this calc's client +
  // section instead (ClientEditForm already scroll-highlights both).
  // A2-581 screen map lead (verified): with no client on the calc this built
  // "/admin/clients&section=ioss" - no "?", so the section never opened.
  const clientEditUrl = calc?.client_id
    ? `/admin/clients?edit=${encodeURIComponent(calc.client_id)}`
    : "/admin/clients?";
  const msiPrereq = readiness.hasIossRows && {
    id: "msi",
    label: t("ui.readiness.msi"),
    done: readiness.msiConfigured,
    sublabel: readiness.msiConfigured
      ? t("ui.readiness.msi_done")
      : t("ui.readiness.msi_todo"),
    gotoUrl: `${clientEditUrl}&section=ioss`,
    gotoLabel: "client IOSS settings",
  };
  const imPrereq = readiness.hasIossRows && {
    id: "im",
    label: t("ui.readiness.im"),
    done: readiness.imNumberConfigured,
    sublabel: readiness.imNumberConfigured
      ? t("ui.readiness.im_done")
      : t("ui.readiness.im_todo"),
    gotoUrl: `${clientEditUrl}&section=ioss`,
    gotoLabel: "client IOSS settings",
  };
  const ossRegPrereq = readiness.hasOssRows && {
    id: "oss-reg",
    label: t("ui.readiness.oss"),
    done: readiness.hasOssRegistration,
    sublabel: readiness.hasOssRegistration
      ? t("ui.readiness.oss_done")
      : t("ui.readiness.oss_todo"),
    gotoUrl: `${clientEditUrl}&section=oss`,
    gotoLabel: "client OSS registration",
  };
  const signoffPrereqs = [reviewFlagsPrereq, fxPrereq];
  const xlsxPrereqs = [reconPrereq];
  const iossPrereqs = [msiPrereq, imPrereq].filter(Boolean);
  const ossPrereqs = [ossRegPrereq].filter(Boolean);
  const allPrereqs = [
    ...signoffPrereqs, ...xlsxPrereqs, ...iossPrereqs, ...ossPrereqs,
  ];
  const prereqsReady = allPrereqs.filter((p) => p.done).length;
  const prereqsTotal = allPrereqs.length;

  return (
    <section data-testid="step-6-freeze-export" className="space-y-3">
      {frozen && (calc?.carry_forward_snapshot_errors || []).length > 0 && (
        <div
          role="alert"
          data-testid="carry-forward-snapshot-errors"
          className="border-2 border-amber-500 bg-amber-50 dark:bg-amber-950/30 rounded-xl px-4 py-3 text-xs text-amber-900 dark:text-amber-100"
        >
          {t("ui.freeze.carry_forward_failed", {
            countries: calc.carry_forward_snapshot_errors.map((e) => e.country).join(", "),
            defaultValue:
              "The return is frozen, but its closing balance could not be saved for {{countries}}. The next period will not open with the right carried-forward credit: check it there before filing.",
          })}
        </div>
      )}
      {/* A2-581 lead 23: a calc replaced BEFORE it was locked (a re-run, or
          a country-form route/drop) had no banner here and an enabled lock
          the server refuses. */}
      {calc?.superseded_by_calc_id && (
        <div
          data-testid="superseded-banner"
          className="flex items-center justify-between gap-3 border-2 border-amber-500 bg-amber-50 dark:bg-amber-950/30 rounded-xl px-4 py-3"
        >
          <div className="text-xs text-amber-900 dark:text-amber-100">
            <span className="font-bold">{frozen
              ? t("ui.freeze.superseded_title", { defaultValue: "This filing has been superseded." })
              : t("ui.freeze.replaced_title", { defaultValue: "A newer calculation has replaced this one; it cannot be locked." })}</span>{" "}
            {t("ui.freeze.superseded_body", {
              defaultValue: "It is kept for the audit trail but is no longer the current calculation for this period.",
            })}
            {/* A2-581 lead 32 (operator 2026-11-02): a replaced filing keeps its exports as the audit copy. */}
            {frozen && (
              <span data-testid="superseded-downloads-note">{" "}{t("ui.freeze.superseded_downloads", {
                defaultValue: "The downloads below are kept as the audit copy of this filing. File from the current calculation.",
              })}</span>
            )}
          </div>
          <button
            type="button"
            data-testid="superseded-goto-current"
            onClick={() => nav(`/?calc_id=${calc.superseded_by_calc_id}`)}
            className="shrink-0 bg-amber-600 hover:bg-amber-700 text-white font-semibold text-[10px] px-3 py-2 rounded-xl"
          >
            {t("ui.freeze.superseded_goto", { defaultValue: "View current calculation →" })}
          </button>
        </div>
      )}
      <ManualRateCorrectionsChip corrections={calc?.manual_rate_corrections} />
      <FilingChecklist
        prereqsReady={prereqsReady} prereqsTotal={prereqsTotal} signedOff={signedOff} calc={calc}
        signoffPrereqs={signoffPrereqs} setActiveStep={setActiveStep} frozen={frozen} filingRef={filingRef}
        handleCopyReference={handleCopyReference} copied={copied} freezeError={freezeError} handleFreeze={handleFreeze}
        freezing={freezing} hasOssRows={hasOssRows} lint={lint} setLintModalOpen={setLintModalOpen}
        fcSummary={fcSummary} allowed={allowed} exportBlockTip={exportBlockTip} xlsxPrereqs={xlsxPrereqs}
        exporting={exporting} exportError={exportError} handleExport={handleExport} hasIossRows={hasIossRows} iossPrereqs={iossPrereqs}
        iossExporting={iossExporting} iossExportError={iossExportError} handleIossExport={handleIossExport}
        ossPrereqs={ossPrereqs} ossExporting={ossExporting} ossExportError={ossExportError}
        handleOssExport={handleOssExport} hasNonUnionOssRows={hasNonUnionOssRows} nonUnionOssExporting={nonUnionOssExporting}
        nonUnionOssExportError={nonUnionOssExportError} handleNonUnionOssExport={handleNonUnionOssExport}
        handleSupersede={handleSupersedeClick} superseded={!!calc?.superseded_by_calc_id} freezeBlockedReason={lockBlock} supersedeBlockedReason={sup.blocked}
      />
      <ReconciliationPanel />
      <FilingPackagePanel calcId={calc?.id} />
      <ExportSafetyNotice safety={exportSafety} />
      <ExportErrorNotice error={exportError} breaches={exportBreaches} onOpen={(cc) => nav(`/calc/${calc.id}/country-return/${cc}`, { state: returnState(location) })} />
      <OssLintReviewModal
        open={lintModalOpen}
        offenders={lint.offenders || []}
        calcId={calc?.id}
        calcFrozen={frozen}
        onClose={() => setLintModalOpen(false)}
        onLintUpdated={(fresh) => setLint(fresh)}
      />

      <FreezeConfirmDialog
        open={freezeConfirmOpen}
        onOpenChange={(o) => { setFreezeConfirmOpen(o); if (o) { setRepaymentConfirmed(false); setMismatchAcknowledged(false); } }}
        coverage={coverage} fxAmend={fxAmend}
        hasNetNegative={hasNetNegative} negativeCountries={negativeCountries}
        repaymentConfirmed={repaymentConfirmed} setRepaymentConfirmed={setRepaymentConfirmed}
        hasRefundChannelMismatch={hasRefundChannelMismatch} refundChannelMismatchCount={refundChannelMismatchCount}
        mismatchAcknowledged={mismatchAcknowledged} setMismatchAcknowledged={setMismatchAcknowledged}
        freezeError={freezeError} freezing={freezing} doFreeze={doFreeze}
      />

      <SupersedeConfirmDialog
        open={supersedeOpen} onOpenChange={setSupersedeOpen}
        filingRef={filingRef} calcId={calc?.id}
        reason={supersedeReason} setReason={setSupersedeReason}
        onValidityChange={setSupersedeValid} valid={supersedeValid}
        error={supersedeError} busy={superseding} onConfirm={doSupersede} carriesFormInputs={sup.carries}
      />
    </section>
  );
};

